<template>
  <div class="pb-10">
    <!-- En-tete -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <button
          class="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:border-primary hover:text-primary transition-colors"
          @click="$router.push({ name: 'proforma-invoices' })">
          <i class="fas fa-arrow-left text-sm"></i>
        </button>
        <div>
          <h1 class="text-lg font-semibold text-gray-900">Details de la facture proforma</h1>
          <p v-if="proforma.proforma_number" class="text-xs text-gray-400">{{ proforma.proforma_number }}</p>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          @click="previewPdf" :disabled="isDownloading">
          <i class="fas fa-eye text-[11px]"></i>
          Apercu
        </button>
        <button
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          @click="downloadPdf" :disabled="isDownloading">
          <i class="fas fa-download text-[11px]"></i>
          PDF
        </button>
        <button
          v-if="canConvert"
          v-permission="'make-sale'"
          class="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary-dark rounded-lg transition-colors"
          @click="showConvertModal = true">
          <i class="fas fa-cart-shopping text-[11px]"></i>
          Convertir en vente
        </button>
      </div>
    </div>

    <!-- Loader -->
    <div v-if="loading" class="bg-white rounded-xl border border-gray-200 p-16 flex flex-col items-center justify-center">
      <div class="w-8 h-8 border-[3px] border-gray-200 border-t-primary rounded-full animate-spin mb-3"></div>
      <p class="text-sm text-gray-400">Chargement...</p>
    </div>

    <!-- Contenu principal -->
    <div v-else-if="proforma.id" class="grid grid-cols-1 lg:grid-cols-3 gap-5">

      <!-- ===== COLONNE GAUCHE (2/3) ===== -->
      <div class="lg:col-span-2 space-y-5">

        <!-- Carte info proforma -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <div class="flex items-start justify-between mb-5">
            <div>
              <h2 class="text-base font-bold text-gray-900 font-mono">{{ proforma.proforma_number }}</h2>
              <p class="text-xs text-gray-400 mt-0.5">{{ formatDate(proforma.proforma_date) }}</p>
            </div>
            <span :class="[
              'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold',
              getStatusClass(proforma.status)
            ]">
              {{ getStatusLabel(proforma.status) }}
            </span>
          </div>

          <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Client</p>
              <p class="text-sm font-medium text-gray-900">{{ proforma.customer?.name || 'Non renseigne' }}</p>
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Cree par</p>
              <p class="text-sm font-medium text-gray-900">{{ proforma.user?.name || 'N/A' }}</p>
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Date de validite</p>
              <p class="text-sm font-medium text-gray-900">{{ proforma.validity_date ? formatDate(proforma.validity_date) : '-' }}</p>
            </div>
            <div>
              <p class="text-[11px] text-gray-400 uppercase tracking-wider mb-0.5">Montant TTC</p>
              <p class="text-sm font-bold text-gray-900">{{ formatCurrency(proforma.total_incl_tax) }}</p>
            </div>
          </div>
        </div>

        <!-- Tableau articles -->
        <div class="bg-white rounded-xl border border-gray-200">
          <div class="px-5 py-4 border-b border-gray-100">
            <h3 class="text-sm font-semibold text-gray-700">Articles <span class="text-gray-400 font-normal">({{ proformaItems.length }})</span></h3>
          </div>

          <div class="divide-y divide-gray-50">
            <div v-for="item in proformaItems" :key="item.id"
              class="flex items-center gap-4 px-5 py-3 hover:bg-gray-50/60 transition-colors">
              <!-- Image -->
              <div class="w-10 h-10 rounded-lg overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                <img v-if="item.product && item.product.image_url" :src="item.product.image_url" :alt="item.product?.name"
                  class="w-full h-full object-cover" />
                <i v-else class="fas fa-image text-gray-300 text-xs"></i>
              </div>
              <!-- Nom + prix unitaire -->
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-gray-900 truncate">{{ item.product?.name || 'Produit inconnu' }}</p>
                <p class="text-xs text-gray-400">{{ formatCurrency(item.price_excl_tax || item.unit_price) }} x {{ item.quantity }}</p>
              </div>
              <!-- Note -->
              <span v-if="item.note"
                class="relative group inline-flex items-center justify-center w-4 h-4 rounded bg-amber-50 cursor-help flex-shrink-0"
                :aria-label="`Note: ${item.note}`">
                <i class="fas fa-exclamation text-[9px] text-amber-400"></i>
                <span
                  class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-normal max-w-xs opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                  {{ item.note }}
                </span>
              </span>
              <!-- Total -->
              <div class="text-sm font-bold text-gray-900 flex-shrink-0">
                {{ formatCurrency(item.total_amount || (item.price_excl_tax * item.quantity)) }}
              </div>
            </div>
          </div>

          <!-- Etat vide articles -->
          <div v-if="proformaItems.length === 0" class="p-8 text-center text-sm text-gray-400">
            Aucun article
          </div>

          <!-- Totaux -->
          <div class="px-5 py-4 border-t border-gray-100 space-y-2">
            <div v-if="parseFloat(proforma.discount) > 0" class="flex justify-between text-sm">
              <span class="text-gray-500">Remise</span>
              <span class="font-medium text-red-500">-{{ formatCurrency(proforma.discount) }}</span>
            </div>
            <div class="flex justify-between items-center pt-2 border-t border-gray-100">
              <span class="text-sm font-bold text-gray-900">Total TTC</span>
              <span class="text-lg font-bold text-gray-900">{{ formatCurrency(proforma.total_incl_tax) }}</span>
            </div>
          </div>
        </div>

        <!-- Notes -->
        <div v-if="proforma.notes" class="bg-white rounded-xl border border-gray-200 p-5">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Notes</h3>
          <div class="bg-gray-50 rounded-lg p-4">
            <p class="text-sm text-gray-600 whitespace-pre-line">{{ proforma.notes }}</p>
          </div>
        </div>

        <!-- Conditions -->
        <div v-if="proforma.terms" class="bg-white rounded-xl border border-gray-200 p-5">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Conditions</h3>
          <div class="bg-gray-50 rounded-lg p-4">
            <p class="text-sm text-gray-600 whitespace-pre-line">{{ proforma.terms }}</p>
          </div>
        </div>
      </div>

      <!-- ===== COLONNE DROITE (1/3) ===== -->
      <div class="space-y-5">

        <!-- Carte statut -->
        <div class="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <!-- Status banner -->
          <div :class="[
            'px-5 py-3 border-b',
            proforma.status === 'accepted' ? 'bg-green-50 border-green-100' :
            proforma.status === 'rejected' ? 'bg-red-50 border-red-100' :
            'bg-gray-50 border-gray-100'
          ]">
            <div class="flex items-center justify-between">
              <h3 :class="[
                'text-sm font-semibold',
                proforma.status === 'accepted' ? 'text-green-700' :
                proforma.status === 'rejected' ? 'text-red-700' :
                'text-gray-700'
              ]">{{ getStatusTitle }}</h3>
            </div>
          </div>

          <div class="p-5 space-y-3">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Total TTC</span>
              <span class="text-sm font-bold text-gray-900">{{ formatCurrency(proforma.total_incl_tax) }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Articles</span>
              <span class="text-sm font-medium text-gray-900">{{ proformaItems.length }}</span>
            </div>
            <div v-if="proforma.validity_date" class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Expire le</span>
              <span :class="[
                'text-sm font-medium',
                isExpired ? 'text-red-500' : 'text-gray-900'
              ]">{{ formatDate(proforma.validity_date) }}</span>
            </div>
            <div v-if="proforma.customer" class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Client</span>
              <span class="text-sm font-medium text-gray-900">{{ proforma.customer.name }}</span>
            </div>
          </div>
        </div>

        <!-- Vente associee -->
        <div v-if="proforma.sale" class="bg-white rounded-xl border border-gray-200 p-5">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-sm font-semibold text-gray-700">Vente associee</h3>
            <span class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-green-50 text-green-700">
              Convertie
            </span>
          </div>

          <div class="space-y-2.5">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">N° Facture</span>
              <span class="text-sm font-semibold text-gray-900 font-mono">{{ proforma.sale.invoice_number }}</span>
            </div>
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Montant</span>
              <span class="text-sm font-medium text-gray-900">{{ formatCurrency(proforma.sale.total_incl_tax) }}</span>
            </div>
          </div>

          <button class="w-full mt-4 pt-3 border-t border-gray-100 text-sm font-medium text-primary hover:text-primary-dark transition-colors text-center"
            @click="viewSaleDetails">
            Voir la vente →
          </button>
        </div>

        <!-- Infos client -->
        <div v-if="proforma.customer" class="bg-white rounded-xl border border-gray-200 p-5">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Client</h3>

          <div class="space-y-2.5">
            <div class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Nom</span>
              <span class="text-sm font-medium text-gray-900">{{ proforma.customer.name }}</span>
            </div>
            <div v-if="proforma.customer.phone" class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Telephone</span>
              <span class="text-sm text-gray-900">{{ proforma.customer.phone }}</span>
            </div>
            <div v-if="proforma.customer.email" class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Email</span>
              <span class="text-sm text-gray-900">{{ proforma.customer.email }}</span>
            </div>
            <div v-if="proforma.customer.address" class="flex justify-between items-center">
              <span class="text-xs text-gray-400">Adresse</span>
              <span class="text-sm text-gray-900">{{ proforma.customer.address }}</span>
            </div>
          </div>
        </div>

        <!-- Actions rapides -->
        <div class="bg-white rounded-xl border border-gray-200 p-5">
          <h3 class="text-sm font-semibold text-gray-700 mb-3">Actions</h3>

          <div class="space-y-1.5">
            <button v-if="canConvert"
              v-permission="'make-sale'"
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-primary hover:bg-primary-50 rounded-lg transition-colors"
              @click="showConvertModal = true">
              <i class="fas fa-cart-shopping text-xs w-4 text-center"></i>
              Convertir en vente
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="duplicateProforma" :disabled="isDuplicating">
              <i v-if="isDuplicating" class="fas fa-spinner fa-spin text-xs w-4 text-center"></i>
              <i v-else class="fas fa-copy text-xs w-4 text-center"></i>
              Dupliquer
            </button>
            <button v-if="proforma.status === 'draft'"
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              @click="rejectProforma" :disabled="isRejecting">
              <i v-if="isRejecting" class="fas fa-spinner fa-spin text-xs w-4 text-center"></i>
              <i v-else class="fas fa-ban text-xs w-4 text-center"></i>
              Rejeter
            </button>
            <button
              class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
              @click="previewPdf" :disabled="isDownloading">
              <i class="fas fa-file-pdf text-xs w-4 text-center"></i>
              Telecharger PDF
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Proforma introuvable -->
    <div v-else-if="!loading && !proforma.id" class="bg-white rounded-xl border border-gray-200 py-16 px-6 text-center">
      <div class="text-gray-300 mb-4">
        <i class="fas fa-exclamation-circle text-4xl"></i>
      </div>
      <h2 class="text-base font-semibold text-gray-700 mb-1">Facture proforma introuvable</h2>
      <p class="text-sm text-gray-400 mb-6">La facture proforma que vous recherchez n'existe pas.</p>
      <button
        class="px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-colors"
        @click="$router.push({ name: 'proforma-invoices' })">
        Retour aux proformas
      </button>
    </div>

    <!-- Modal de confirmation conversion -->
    <BaseModal
      v-model="showConvertModal"
      size="lg"
      :closable="!isConverting"
      @close="showConvertModal = false"
    >
      <template #header>
        <div class="flex items-center gap-3">
          <div class="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center text-primary">
            <i class="fas fa-cart-shopping text-lg"></i>
          </div>
          <div>
            <h2 class="text-lg font-semibold text-gray-900">Convertir en vente</h2>
            <p class="text-xs text-gray-400">{{ proforma.proforma_number }}</p>
          </div>
        </div>
      </template>

      <div class="space-y-5">
        <!-- Message d'erreur -->
        <div v-if="convertError" class="bg-red-50 border border-red-100 rounded-lg p-3 flex items-start gap-2.5">
          <i class="fas fa-exclamation-circle text-red-500 mt-0.5 text-sm flex-shrink-0"></i>
          <span class="text-sm text-red-600">{{ convertError }}</span>
        </div>

        <!-- Info -->
        <div class="bg-amber-50 border border-amber-100 rounded-lg p-3 flex items-start gap-2.5">
          <i class="fas fa-info-circle text-amber-500 mt-0.5 text-sm flex-shrink-0"></i>
          <span class="text-sm text-amber-700">Cette action va creer une vente differee a partir de cette proforma. Cette operation est irreversible.</span>
        </div>

        <!-- Resume client -->
        <div v-if="proforma.customer">
          <h4 class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Client</h4>
          <div class="bg-gray-50 border border-gray-100 rounded-lg p-3">
            <p class="text-sm font-medium text-gray-900">{{ proforma.customer.name }}</p>
            <p v-if="proforma.customer.phone" class="text-xs text-gray-400 mt-0.5">{{ proforma.customer.phone }}</p>
          </div>
        </div>

        <!-- Resume articles -->
        <div>
          <h4 class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Articles ({{ proformaItems.length }})</h4>
          <div class="border border-gray-100 rounded-lg overflow-hidden">
            <div class="divide-y divide-gray-50 max-h-48 overflow-y-auto">
              <div v-for="item in proformaItems" :key="item.id"
                class="flex items-center justify-between gap-3 px-3 py-2.5">
                <div class="flex items-center gap-3 flex-1 min-w-0">
                  <div class="w-8 h-8 rounded-md overflow-hidden bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <img v-if="item.product && item.product.image_url" :src="item.product.image_url" :alt="item.product?.name"
                      class="w-full h-full object-cover" />
                    <i v-else class="fas fa-image text-gray-300 text-[10px]"></i>
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-gray-900 truncate">{{ item.product?.name || 'Produit inconnu' }}</p>
                    <p class="text-xs text-gray-400">{{ formatCurrency(item.price_excl_tax || item.unit_price) }} x {{ item.quantity }}</p>
                  </div>
                </div>
                <span class="text-sm font-semibold text-gray-900 flex-shrink-0">
                  {{ formatCurrency(item.total_amount || (item.price_excl_tax * item.quantity)) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Recapitulatif montant -->
        <div class="bg-gray-50 border border-gray-100 rounded-xl p-4">
          <h4 class="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Recapitulatif</h4>
          <div class="space-y-2">
            <div class="flex justify-between text-sm text-gray-500">
              <span>Nombre d'articles</span>
              <span>{{ proformaItems.length }}</span>
            </div>
            <div v-if="parseFloat(proforma.discount) > 0" class="flex justify-between text-sm text-gray-500">
              <span>Remise</span>
              <span class="text-red-500">-{{ formatCurrency(proforma.discount) }}</span>
            </div>
            <div class="flex justify-between text-sm font-bold text-gray-900 pt-2 border-t border-gray-200">
              <span>Montant total TTC</span>
              <span class="text-primary">{{ formatCurrency(proforma.total_incl_tax) }}</span>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex items-center justify-end gap-3 w-full">
          <BaseButton variant="ghost" :disabled="isConverting" @click="showConvertModal = false">
            Annuler
          </BaseButton>
          <BaseButton
            variant="primary"
            :loading="isConverting"
            @click="confirmConvertToSale"
          >
            <template #icon-left>
              <i class="fas fa-cart-shopping text-xs"></i>
            </template>
            Confirmer la conversion
          </BaseButton>
        </div>
      </template>
    </BaseModal>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { proformaInvoiceService } from '@/services'
import CashRegisterService from '@/services/cashRegisterService'
import { useToastStore } from '@/store/modules/toastStore'
import BaseModal from '@/components/base/BaseModal.vue'
import BaseButton from '@/components/base/BaseButton.vue'

export default defineComponent({
  name: 'ProformaInvoiceDetailsView',
  components: {
    BaseModal,
    BaseButton
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const toastStore = useToastStore()

    const proforma = ref({})
    const loading = ref(true)
    const isDownloading = ref(false)
    const isConverting = ref(false)
    const isDuplicating = ref(false)
    const isRejecting = ref(false)
    const showConvertModal = ref(false)
    const convertError = ref('')

    const proformaItems = computed(() => proforma.value.items || [])

    const canConvert = computed(() => {
      return proformaInvoiceService.canBeConverted(proforma.value)
    })

    const isExpired = computed(() => {
      if (!proforma.value.validity_date) return false
      return new Date(proforma.value.validity_date) < new Date()
    })

    const getStatusTitle = computed(() => {
      if (proforma.value.status === 'accepted') return 'Proforma acceptee'
      if (proforma.value.status === 'rejected') return 'Proforma rejetee'
      return 'Brouillon'
    })

    const loadProformaDetails = async () => {
      const proformaId = route.params.id
      if (!proformaId) {
        router.push({ name: 'proforma-invoices' })
        return
      }

      try {
        loading.value = true
        const response = await proformaInvoiceService.getProformaInvoice(proformaId, {
          with: ['customer', 'user', 'items', 'items.product', 'sale']
        })
        proforma.value = response.data?.data || response.data || {}
      } catch (err) {
        console.error('Erreur chargement proforma:', err)
        proforma.value = {}
      } finally {
        loading.value = false
      }
    }

    const formatCurrency = (amount) => {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(amount || 0)
    }

    const formatDate = (dateString) => {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }

    const getStatusLabel = (status) => {
      return proformaInvoiceService.getStatusLabel(status)
    }

    const getStatusClass = (status) => {
      const classes = {
        'draft': 'bg-gray-100 text-gray-600',
        'accepted': 'bg-green-50 text-green-700',
        'rejected': 'bg-red-50 text-red-700'
      }
      return classes[status] || 'bg-gray-100 text-gray-600'
    }

    const previewPdf = async () => {
      if (isDownloading.value) return
      try {
        isDownloading.value = true
        await proformaInvoiceService.downloadPdf(proforma.value.id, true)
      } catch (err) {
        console.error('Erreur apercu PDF:', err)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors de l\'apercu du PDF',
          duration: 4000
        })
      } finally {
        isDownloading.value = false
      }
    }

    const downloadPdf = async () => {
      if (isDownloading.value) return
      try {
        isDownloading.value = true
        await proformaInvoiceService.downloadPdf(proforma.value.id, false)
      } catch (err) {
        console.error('Erreur telechargement PDF:', err)
        toastStore.addToast({
          type: 'error',
          message: 'Erreur lors du telechargement du PDF',
          duration: 4000
        })
      } finally {
        isDownloading.value = false
      }
    }

    const confirmConvertToSale = async () => {
      if (isConverting.value || !canConvert.value) return

      convertError.value = ''

      try {
        isConverting.value = true

        // Recuperer la caisse ouverte
        const cashRegister = await CashRegisterService.getMyOpenedCashRegister()
        if (!cashRegister?.data?.id) {
          convertError.value = 'Aucune caisse ouverte. Veuillez ouvrir une caisse avant de convertir.'
          return
        }

        await proformaInvoiceService.convertToSale(proforma.value.id, {
          cash_register_id: cashRegister.data.id
        })

        showConvertModal.value = false

        toastStore.addToast({
          type: 'success',
          message: 'Proforma convertie en vente avec succes',
          duration: 4000
        })

        // Recharger les details pour voir le statut mis a jour
        await loadProformaDetails()

      } catch (error) {
        console.error('Erreur conversion:', error)
        convertError.value = error.response?.data?.message || 'Erreur lors de la conversion en vente'
      } finally {
        isConverting.value = false
      }
    }

    const duplicateProforma = async () => {
      if (isDuplicating.value) return

      try {
        isDuplicating.value = true
        const response = await proformaInvoiceService.duplicateProformaInvoice(proforma.value.id)

        toastStore.addToast({
          type: 'success',
          message: 'Proforma dupliquee avec succes',
          duration: 4000
        })

        // Rediriger vers la nouvelle proforma
        const newProforma = response.data?.data || response.data
        if (newProforma?.id) {
          router.push({ name: 'proforma-invoice-details', params: { id: newProforma.id } })
        }
      } catch (error) {
        console.error('Erreur duplication:', error)
        const errMsg = error.response?.data?.message || 'Erreur lors de la duplication'
        toastStore.addToast({
          type: 'error',
          message: errMsg,
          duration: 4000
        })
      } finally {
        isDuplicating.value = false
      }
    }

    const rejectProforma = async () => {
      if (isRejecting.value) return

      try {
        isRejecting.value = true
        await proformaInvoiceService.rejectProformaInvoice(proforma.value.id)

        toastStore.addToast({
          type: 'success',
          message: 'Proforma rejetee',
          duration: 4000
        })

        await loadProformaDetails()
      } catch (error) {
        console.error('Erreur rejet:', error)
        const errMsg = error.response?.data?.message || 'Erreur lors du rejet'
        toastStore.addToast({
          type: 'error',
          message: errMsg,
          duration: 4000
        })
      } finally {
        isRejecting.value = false
      }
    }

    const viewSaleDetails = () => {
      if (proforma.value.sale?.id) {
        router.push({ name: 'sale-details', params: { id: proforma.value.sale.id } })
      }
    }

    onMounted(() => {
      loadProformaDetails()
    })

    return {
      proforma,
      loading,
      isDownloading,
      isConverting,
      isDuplicating,
      isRejecting,
      showConvertModal,
      convertError,
      proformaItems,
      canConvert,
      isExpired,
      getStatusTitle,
      formatCurrency,
      formatDate,
      getStatusLabel,
      getStatusClass,
      previewPdf,
      downloadPdf,
      confirmConvertToSale,
      duplicateProforma,
      rejectProforma,
      viewSaleDetails
    }
  }
})
</script>
